import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import TablePagination from '@material-ui/core/TablePagination';
import TextField from '@components/TextFieldCaseSetting';

import { LinkCellRenderer } from '@trueprodigy/components';
import AgGrid from '@components/AgGrid/AgGrid';
import PropTypes from 'prop-types';

export const renderMetadata = (metadata, delimiter = ' / ') =>
  Object.entries(metadata).reduce((agg, [key, value], i) => {
    agg += ` ${key} - ${value}${i < Object.keys(metadata).length - 1 ? delimiter : ''
      } `;
    return agg;
  }, '');

const SummaryListing = ({
  revisionHistory,
  recordCount,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  setId,
  filter,
  setFilter
}) => {

  const [gridapi, setGridapi] = useState(null);

  useEffect(() => {
    if (gridapi) {
      gridapi.setQuickFilter(filter);
    }
  }, [gridapi, filter]);

  const rowData = revisionHistory
    ? revisionHistory.map(
      i =>
        i && {
          id: i.revisionHistoryID,
          dateTime: i.changeDt,
          user: i.changeBy,
          table: i.tableName,
          action: i.userAction,
          metadata: renderMetadata(i.metaData),
        },
    )
    : [];

  const getColumnDefs = () => {
    const columnDefs = [
      {
        headerName: 'Id',
        field: 'id',
        width: 90,
        cellRenderer: LinkCellRenderer,
        cellRendererParams: {
          onClick: id => setId(id),
        },
        sort: 'desc',
      },
      {
        headerName: 'Date Time',
        field: 'dateTime',
        width: 150,
        valueFormatter: param =>
          moment(param.value).format('MMM D, YYYY H:mm:ss A'),
      },
      {
        headerName: 'User',
        field: 'user',
        width: 210,
      },
      {
        headerName: 'Table',
        field: 'table',
        width: 150,
      },
      {
        headerName: 'Action',
        field: 'action',
        width: 130,
      },
      {
        headerName: 'Metadata',
        field: 'metadata',
      },
    ];
    return columnDefs;
  };

  return (
    <Container>
      <HeaderContainer>
        <Title>Revision History</Title>
        <div>
          <TextField
            value={filter}
            style={{
              width: '200px',
              flexGrow: 1,
              marginRight: '20px',
            }}
            placeholder="Search"
            onChange={e => setFilter(e.target.value)}
          />
        </div>
      </HeaderContainer>
      <TableContainer className="ag-theme-material">
        <AgGrid
          columnDefs={getColumnDefs()}
          disablePreferenceSave
          autoResizeToFit
          defaultColDef={{ resizable: true, sortable: true }}
          rowData={rowData}
          name="revisionHistorySummary"
          onGridReady={grid => {
            setGridapi(grid.api);
          }}
        />
      </TableContainer>
      <TablePagination
        backIconButtonProps={{
          'aria-label': 'Previous Page',
        }}
        component="div"
        count={recordCount}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
        }}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 50, 100, 500]}
        onChangePage={(event, page) => setPage(page)}
        onChangeRowsPerPage={({ target }) => {
          const rows = target.value;
          setRowsPerPage(rows);
          setPage(0);
        }}
      />
    </Container>
  );
};

SummaryListing.propTypes = {
  revisionHistory: PropTypes.arrayOf(PropTypes.object),
  setId: PropTypes.func.isRequired,
  recordCount: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  setPage: PropTypes.func.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  filter: PropTypes.any,
  setFilter: PropTypes.func.isRequired,
};

const Container = styled.div`
  height: 100%;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  padding: 0 20px;
  height: 56px;
  font-family: Roboto, sans-serif;
  visibility: ${({ preview }) => (preview ? 'hidden' : 'visible')};
`;

const Title = styled.h4`
  flex: ${({ flex }) => flex || 1};
  font-family: Roboto, sans-serif;
  visibility: visible;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.87);
`;

const TableContainer = styled.div`
  height: calc(100vh - 400px);
  margin: 20px;
`;

export default SummaryListing;
